import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@ui-kit';
import { getStoredToken } from '../login/api/loginApi';
import { useMe } from './hooks/useMe';

interface ManagerRouteGuardProps {
  children: ReactNode;
}

const ROLE_RANK: Record<string, number> = {
  ROLE_GUEST: -1,
  ROLE_USER: 0,
  ROLE_MANAGER: 1,
  ROLE_ADMIN: 2,
  /** #405 fix (UAT 2026-05-11): ROLE_EXEC 는 ROLE_ADMIN 이상 — 전 화면 열람 가능. */
  ROLE_EXEC: 3,
};

/**
 * R7-025: ROLE_MANAGER 이상 전용 경로 보호.
 *
 * catalog 의 sidebarRole=ROLE_MANAGER 항목(common-code, audit-log, menu-management)은
 * ROLE_MANAGER 와 ROLE_ADMIN 모두 접근 가능해야 한다.
 * AdminRouteGuard 는 ROLE_ADMIN 만 허용하므로 별도 가드 생성.
 */
export function ManagerRouteGuard({ children }: ManagerRouteGuardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = getStoredToken();
  const meQuery = useMe();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (meQuery.isLoading) {
    return null;
  }

  if (meQuery.isError || !meQuery.data) {
    return <Navigate to="/login" replace />;
  }

  const userRank = ROLE_RANK[meQuery.data.role] ?? 0;
  const requiredRank = ROLE_RANK['ROLE_MANAGER'] ?? 1;

  if (userRank < requiredRank) {
    return (
      <ErrorState
        title={t('gateway.shared.error.forbidden')}
        message={t('gateway.shared.error.forbiddenMessage')}
        actions={[
          {
            label: t('gateway.shared.error.actions.goHome'),
            onClick: () => navigate('/'),
            variant: 'primary',
          },
        ]}
      />
    );
  }

  return <>{children}</>;
}
